import type { TrackStyle, ShadowPreset, BoxShadowPreset, StylePresetPreview } from './stylePresets.ts';

/** Cap keeps extension sync storage under QUOTA_BYTES_PER_ITEM (~8KB). */
export const MAX_CUSTOM_STYLE_PRESETS = 20;

/** Soft budget under Chrome's 8192-byte sync item limit (UTF-16-ish JSON). */
export const CUSTOM_STYLE_PRESETS_SYNC_SOFT_LIMIT = 7800;

export type CustomStylePreset = {
  id: string;
  name: string;
  style: TrackStyle;
  /** Optional Style Studio preview copy (saved with the preset). */
  preview?: StylePresetPreview;
};

/** Fill-ins for incomplete custom presets (new installs / missing fields only). */
const STYLE_DEFAULTS: TrackStyle = {
  fontSizeRatio: 1,
  fontWeight: 500,
  fontColor: '#FFFFFF',
  strokeWidth: 0,
  strokeColor: '#000000',
  shadow: '',
  shadowColor: '#000000',
  fontBackgroundColor: '#000000',
  fontBackgroundColorOpacity: 0.5,
  fontFamily: 'Netflix Sans,Helvetica Neue,Segoe UI,Roboto,Ubuntu,sans-serif',
  fontGoogleFamilies: '',
  fontGoogleCssProperties: '',
  letterSpacing: 0,
  lineHeight: 1.35,
  padding: 8,
  opacity: 1,
  boxShadow: '',
};

const SHADOWS = new Set<ShadowPreset>(['', 'raised', 'depressed', 'outline', 'shadow']);
const BOX_SHADOWS = new Set<BoxShadowPreset>(['', 'soft', 'medium']);
const PREVIEW_TEXT_MAX = 200;
const PREVIEW_THUMB_MAX = 8;

export function createCustomStylePresetId(): string {
  return `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function asNumber(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

/** Short thumb from main preview line (Unicode-aware). */
export function previewThumbFromText(text: string, fallback = 'Aa'): string {
  const chars = Array.from(text.trim());
  if (chars.length === 0) return fallback;
  return chars.slice(0, 2).join('');
}

export function normalizeStylePresetPreview(raw: unknown): StylePresetPreview | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const main = typeof o.main === 'string' ? o.main.slice(0, PREVIEW_TEXT_MAX) : '';
  const second = typeof o.second === 'string' ? o.second.slice(0, PREVIEW_TEXT_MAX) : '';
  if (!main && !second) return undefined;
  const thumbRaw = typeof o.thumb === 'string' ? o.thumb.trim().slice(0, PREVIEW_THUMB_MAX) : '';
  return {
    main,
    second,
    thumb: thumbRaw || previewThumbFromText(main || second),
  };
}

export function buildStylePresetPreview(
  main: string,
  second: string,
  thumb?: string,
): StylePresetPreview {
  const m = main.slice(0, PREVIEW_TEXT_MAX);
  const s = second.slice(0, PREVIEW_TEXT_MAX);
  const t = (thumb || '').trim().slice(0, PREVIEW_THUMB_MAX);
  return {
    main: m,
    second: s,
    thumb: t || previewThumbFromText(m || s),
  };
}

export function normalizeTrackStyle(raw: unknown): TrackStyle | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const shadow = asString(o.shadow, '') as ShadowPreset;
  const boxShadow = asString(o.boxShadow, '') as BoxShadowPreset;
  return {
    fontSizeRatio: asNumber(o.fontSizeRatio, STYLE_DEFAULTS.fontSizeRatio),
    fontWeight: asNumber(o.fontWeight, STYLE_DEFAULTS.fontWeight),
    fontColor: asString(o.fontColor, STYLE_DEFAULTS.fontColor),
    strokeWidth: asNumber(o.strokeWidth, STYLE_DEFAULTS.strokeWidth),
    strokeColor: asString(o.strokeColor, STYLE_DEFAULTS.strokeColor),
    shadow: SHADOWS.has(shadow) ? shadow : '',
    shadowColor: asString(o.shadowColor, STYLE_DEFAULTS.shadowColor),
    fontBackgroundColor: asString(o.fontBackgroundColor, STYLE_DEFAULTS.fontBackgroundColor),
    fontBackgroundColorOpacity: asNumber(
      o.fontBackgroundColorOpacity,
      STYLE_DEFAULTS.fontBackgroundColorOpacity,
    ),
    fontFamily: asString(o.fontFamily, STYLE_DEFAULTS.fontFamily),
    fontGoogleFamilies: asString(o.fontGoogleFamilies, ''),
    fontGoogleCssProperties: asString(o.fontGoogleCssProperties, ''),
    letterSpacing: asNumber(o.letterSpacing, STYLE_DEFAULTS.letterSpacing),
    lineHeight: asNumber(o.lineHeight, STYLE_DEFAULTS.lineHeight),
    padding: asNumber(o.padding, STYLE_DEFAULTS.padding),
    opacity: asNumber(o.opacity, STYLE_DEFAULTS.opacity),
    boxShadow: BOX_SHADOWS.has(boxShadow) ? boxShadow : '',
  };
}

export function normalizeCustomStylePresets(raw: unknown): CustomStylePreset[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomStylePreset[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const id = typeof row.id === 'string' ? row.id.trim() : '';
    const name = typeof row.name === 'string' ? row.name.trim() : '';
    const style = normalizeTrackStyle(row.style);
    if (!id || !name || !style || seen.has(id)) continue;
    seen.add(id);
    // Language bindings live in styleLanguagePriorities — never on the style body.
    const preview = normalizeStylePresetPreview(row.preview);
    out.push(preview ? { id, name: name.slice(0, 48), style, preview } : { id, name: name.slice(0, 48), style });
    if (out.length >= MAX_CUSTOM_STYLE_PRESETS) break;
  }
  return out;
}

/** UTF-8 byte length of JSON used for Chrome sync item budgeting. */
export function estimateCustomPresetsSyncBytes(presets: readonly CustomStylePreset[]): number {
  return new TextEncoder().encode(JSON.stringify(presets)).length;
}

export function assertCustomPresetsFitSyncQuota(presets: readonly CustomStylePreset[]): void {
  const bytes = estimateCustomPresetsSyncBytes(presets);
  if (bytes > CUSTOM_STYLE_PRESETS_SYNC_SOFT_LIMIT) {
    throw new Error(`QUOTA:${bytes}`);
  }
}

/** Preserve legacy languages[] only long enough to lift into styleLanguagePriorities. */
export function readLegacyPresetLanguages(raw: unknown): Array<{ id: string; languages: unknown }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ id: string; languages: unknown }> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    if (typeof row.id !== 'string' || !row.id.trim()) continue;
    if (row.languages === undefined) continue;
    out.push({ id: row.id.trim(), languages: row.languages });
  }
  return out;
}

export function getCustomStylePreset(
  presets: readonly CustomStylePreset[],
  id: string,
): CustomStylePreset | undefined {
  return presets.find((preset) => preset.id === id);
}

export function nextDefaultCustomStyleName(
  presets: readonly CustomStylePreset[],
  baseName: string,
): string {
  const base = baseName.trim() || 'My style';
  const taken = new Set(presets.map((p) => p.name));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base} ${n}`)) n += 1;
  return `${base} ${n}`;
}

